import React, { useRef, useState } from 'react';
import { Box, ListItemButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { languageMeta } from '../common/constants/language.constants';
import { I18N_LANGUAGE_CODES, UI_LANGUAGES, UiLanguage } from '../i18n/language.config';
import { useUiLanguage } from '../i18n/useUiLanguage';
import { setUiLanguage } from '../i18n';
import { COLLAPSED_ITEM_SX, SIDEBAR_FOCUS_SX } from './SidebarFinanceGroup';

import { palette } from '../theme';
/**
 * The interface language, as one sidebar row that opens a small menu above it.
 *
 * Not the content-language selector used on event configuration - that one picks which
 * translation of a parent-facing text is being edited.
 *
 * Every language is named in itself ("Srpski", "English") rather than through t(), so an
 * admin who cannot read the current interface can still find their own. The menu opens
 * upwards because the row sits at the bottom of the sidebar.
 *
 * Collapsed, the row shrinks to its flag and the same menu opens to the right of it
 * instead - upwards from a 48px square would cover the items above it.
 */
const LanguageSwitch: React.FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const { t } = useTranslation();
  const current = useUiLanguage();
  const rowRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { Flag, nativeName } = languageMeta[current];

  const close = () => {
    setOpen(false);
    // back to the row, so keyboard users are not dropped at the top of the page
    rowRef.current?.focus();
  };

  const choose = (language: UiLanguage) => {
    if (language !== current) setUiLanguage(language);
    close();
  };

  return (
    <>
      {collapsed ? (
        <Tooltip title={`${t('language.menuLabel')}: ${nativeName}`} placement="right">
          <ListItemButton
            ref={rowRef}
            onClick={() => setOpen(previous => !previous)}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={`${t('language.menuLabel')}: ${nativeName}`}
            sx={{ ...COLLAPSED_ITEM_SX, ...(open && { backgroundColor: palette.sidebar.selected }) }}
          >
            <Flag style={{ width: 24, height: 16, borderRadius: 2, flex: 'none' }} />
          </ListItemButton>
        </Tooltip>
      ) : (
        <ListItemButton
          ref={rowRef}
          onClick={() => setOpen(previous => !previous)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${t('language.menuLabel')}: ${nativeName}`}
          sx={{
            height: 48,
            p: '0 12px 0 16px',
            gap: 1,
            ...(open && { backgroundColor: palette.sidebar.selected }),
            '&:hover': { backgroundColor: palette.sidebar.selected },
            ...SIDEBAR_FOCUS_SX
          }}
        >
          <Flag style={{ width: 24, height: 16, borderRadius: 2, flex: 'none' }} />
          <Typography sx={{ flex: 1, fontSize: 16, color: 'white' }}>{nativeName}</Typography>
          <ExpandLessIcon
            sx={{ fontSize: 20, color: palette.sidebar.textMuted, transition: 'transform 150ms ease', transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </ListItemButton>
      )}

      <Menu
        anchorEl={rowRef.current}
        open={open}
        onClose={close}
        anchorOrigin={collapsed ? { vertical: 'bottom', horizontal: 'right' } : { vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: { elevation: 3, sx: { width: 232, borderRadius: '10px', ...(collapsed ? { ml: 1 } : { mb: 1 }) } },
          list: { sx: { py: 1 }, 'aria-label': t('language.menuLabel') }
        }}
      >
        {/* a heading, not an option: it is not focusable and cannot be chosen */}
        <Box component="li" role="presentation" sx={{ p: '8px 16px 4px', fontSize: 12, color: palette.text.secondary, listStyle: 'none' }}>
          {t('language.menuLabel')}
        </Box>
        {UI_LANGUAGES.map(language => {
          const meta = languageMeta[language];
          const selected = language === current;
          return (
            <MenuItem
              key={language}
              role="menuitemradio"
              aria-checked={selected}
              lang={I18N_LANGUAGE_CODES[language]}
              onClick={() => choose(language)}
              sx={{
                height: 44,
                px: 2,
                fontSize: 16,
                color: palette.text.primary,
                gap: 1.5,
                backgroundColor: selected ? palette.surface.tint : undefined,
                '&:hover': { backgroundColor: selected ? palette.surface.tint : palette.surface.hover }
              }}
            >
              <meta.Flag style={{ width: 20, height: 14, borderRadius: 2, flex: 'none' }} />
              <Box component="span" sx={{ flex: 1 }}>
                {meta.nativeName}
              </Box>
              {selected && <CheckIcon sx={{ fontSize: 20, color: palette.sidebar.main }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default LanguageSwitch;
